<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
      <!-- Background overlay -->
      <div class="fixed inset-0 transition-opacity" @click="close">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- Modal panel -->
      <div class="bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-5xl sm:w-full max-h-[90vh] flex flex-col" style="min-width: 1000px;">
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 overflow-y-auto">
          <div class="flex justify-between items-start">
            <div>
              <h3 class="text-xl font-bold text-gray-900">
                Review Forecasting Data
              </h3>
              <p class="text-sm text-gray-500 mt-1">Review and edit the forecast data</p>
            </div>
            
            <button @click="close" class="text-gray-400 hover:text-gray-500">
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Information Card -->
          <div class="mt-4 bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div>
                <h4 class="text-sm font-medium text-gray-700">Assignee</h4>
                <div class="flex items-center mt-1">
                  <div class="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800">
                    SG
                  </div>
                  <span class="ml-2 text-sm text-gray-900">Sara Golob</span>
                </div>
              </div>
              
              <div>
                <h4 class="text-sm font-medium text-gray-700">Due Date</h4>
                <p class="mt-1 text-sm text-gray-900">17/11/2025</p>
              </div>
              
              <div>
                <h4 class="text-sm font-medium text-gray-700">Priority</h4>
                <span class="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Medium
                </span>
              </div>
              
              <div>
                <h4 class="text-sm font-medium text-gray-700">Status</h4>
                <p class="mt-1 text-sm text-gray-900">Adjustment Needed</p>
              </div>
            </div>
            
            <div>
              <h4 class="text-sm font-medium text-gray-700">Description</h4>
              <p class="mt-1 text-sm text-gray-900">Q2 2025 marketing budget report for financial planning.</p>
            </div>
          </div>
          
          <!-- Spreadsheet Container -->
          <div class="mt-6">
            <div style="height: 500px;">
              <KendoSpreadsheet ref="spreadsheetComponent" />
            </div>
          </div>
        </div>
        
        <div class="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end sticky bottom-0">
          <button
            @click="close"
            class="mr-2 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:text-sm"
          >
            Cancel
          </button>
          <button
            @click="saveAndClose"
            class="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from 'vue';
import KendoSpreadsheet from "@/components/KendoSpreadsheet.vue";
import { logger } from '@/services/logger';

// Add type declarations for jQuery
declare global {
  interface Window {
    jQuery: any;
    $: any;
    kendo: any;
  }
}

export default defineComponent({
  name: 'SpreadsheetReviewModal',
  components: {
    KendoSpreadsheet
  },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    }
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    // Define a type for the KendoSpreadsheet component instance
    interface KendoSpreadsheetInstance {
      $el: HTMLElement;
      loadFromFile: (fileData: ArrayBuffer) => boolean;
      loadFromCSV: (csvContent: string) => boolean;
      getSpreadsheet: () => any;
    }
    
    const spreadsheetComponent = ref<KendoSpreadsheetInstance | null>(null);
    
    // Watch for changes to isOpen prop
    watch(() => props.isOpen, (newValue) => {
      if (newValue) {
        // Modal has been opened, ensure spreadsheet is properly initialized
        setTimeout(() => {
          if (spreadsheetComponent.value) {
            // Force re-initialization of the spreadsheet when modal opens
            logger.info('Reinitializing spreadsheet in modal');
            // spreadsheetComponent.value.reinitialize();
          }
        }, 300); // Small delay to ensure DOM is updated
      }
    });
    
    // When component is mounted
    onMounted(() => {
      logger.info('SpreadsheetReviewModal mounted');
    });
    
    function close() {
      emit('close');
    }
    
    function saveAndClose() {
      // Here you would implement the actual save logic
      // For now, we'll just log and emit the save event
      logger.info('Saving spreadsheet data');
      
      if (spreadsheetComponent.value) {
        // Access the spreadsheet data if needed
        logger.info('Spreadsheet component accessed');
      }
      
      // Emit save event
      emit('save');
      
      // Close the modal
      close();
    }
    
    // When component is mounted, load sample data
    onMounted(() => {
      logger.info('SpreadsheetReviewModal mounted');
      
      // Load sample data when the component is mounted
      setTimeout(() => {
        if (spreadsheetComponent.value) {
          // Sample data for Q2 2025 marketing budget
          const sampleData = {
            sheets: [{
              name: "Q2 2025 Marketing Budget",
              columns: [
                { width: 150 }, { width: 100 }, { width: 100 }, { width: 100 }
              ],
              rows: [
                {
                  cells: [
                    { value: "Category", bold: true, background: "#f0f0f0" },
                    { value: "April", bold: true, background: "#f0f0f0" },
                    { value: "May", bold: true, background: "#f0f0f0" },
                    { value: "June", bold: true, background: "#f0f0f0" }
                  ]
                },
                { cells: [{ value: "Digital Marketing" }, { value: 12500 }, { value: 14000 }, { value: 15000 }] },
                { cells: [{ value: "Print Media" }, { value: 8000 }, { value: 7500 }, { value: 7000 }] },
                { cells: [{ value: "Events" }, { value: 20000 }, { value: 15000 }, { value: 25000 }] },
                { cells: [{ value: "Social Media" }, { value: 10000 }, { value: 10500 }, { value: 11000 }] },
                { cells: [{ value: "Content Creation" }, { value: 5000 }, { value: 5500 }, { value: 6000 }] },
                { cells: [{ value: "Email Campaigns" }, { value: 3000 }, { value: 3200 }, { value: 3500 }] },
                { cells: [{ value: "Total", bold: true }, { formula: "SUM(B2:B7)" }, { formula: "SUM(C2:C7)" }, { formula: "SUM(D2:D7)" }] }
              ]
            }]
          };
          
          // Load the sample data into the spreadsheet
          try {
            const $ = window.jQuery;
            if ($) {
              const spreadsheet = $(spreadsheetComponent.value.$el).data('kendoSpreadsheet');
              if (spreadsheet) {
                spreadsheet.fromJSON(sampleData);
                logger.info('Sample data loaded successfully');
              }
            }
          } catch (error) {
            logger.error('Error loading sample data', { error });
          }
        }
      }, 500);
    });

    return {
      spreadsheetComponent,
      close,
      saveAndClose
    };
  }
});
</script>

<style scoped>
/* Ensure the modal has proper height constraints */
.kendo-spreadsheet-container {
  height: 100%;
  max-height: 500px;
}
</style>
